import { z, ZodSchema } from "zod";
import { MeasuringSystem } from "../../types";

export enum OocyteCondition {
  fresh = "fresh",
  frozen = "frozen",
}

export interface OocyteUpload {
  fileUrl: string;
  hph: number | null;
  condition: keyof typeof OocyteCondition | null;
  signedUrl?: string;
  path?: string;
  originalName: string;
  status: "init" | "uploading" | "uploaded" | "error";
}

export interface FormData {
  name: string;
  birth: Date;
  ethnicity: number[];
  height: number;
  weight: number;
  "amh.date"?: Date | null;
  "amh.value"?: number | null;
  "fsh.date"?: Date | null;
  "fsh.value"?: number | null;
  "afc.date"?: Date | null;
  "afc.left"?: number | null;
  "afc.right"?: number | null;
  "retrieval.date"?: Date | null;
  "retrieval.count"?: number | null;
}

export interface FormResponse {
  cycle: { id: string; created: string };
}

export interface GetCycleResponseImage {
  id: number;
  filename: string;
  p_fertilization: number;
  s_fertilization: number;
  p_cleavage: number;
  s_cleavage: number;
  p_blastulation: number;
  s_blastulation: number;
  p_overall: number;
  frozen: boolean | null;
  hph: number | null;
  u_overall: number | null;
  created: string;
  updated: string;
  u_notes: string;
  invalid: boolean;
}

export interface GetCycleResponseItem {
  afc_date: string | null;
  afc_left: number | null;
  afc_right: number | null;
  amh_date: string | null;
  amh_value: number | null;
  birth: string;
  weight: number;
  ethnicity: string[];
  fsh_date: string | null;
  fsh_value: number | null;
  retrieval_date: string | null;
  retrieval_value: number | null;
  height: number;
  id: string;
  patient: string;
  p_aneuploidy: number;
  p_livebirth: number;
  u_fertilized: number;
  p_fertilized: number;
  u_blastocyst: number;
  p_blastulation: number;
  u_livebirth: number;
  images: GetCycleResponseImage[];
  created: string;
  updated: string;
}

export interface GetCycleResponse {
  next_page_token: string | null;
  cycle: GetCycleResponseItem;
}

export const createSchema = (system: MeasuringSystem): ZodSchema<FormData> => {
  return z.object({
    name: z
      .string({ message: "errors.name.required" })
      .min(2, { message: "errors.name.required" })
      .max(32, { message: "errors.name.required" }),
    birth: z.date({ message: "errors.birth.invalid" }),
    height: z
      .number({ message: "errors.height.invalid" })
      .int()
      .min(120, "errors.height.range")
      .max(240, "errors.height.range"),
    weight: z
      .number({ message: "errors.weight.invalid" })
      .int()
      .min(40, "errors.weight.range")
      .max(160, "errors.weight.range"),
    ethnicity: z.array(z.any(), { message: "errors.ethnicity.required" }),
    "amh.date": z.date().nullable().optional(),
    "amh.value": z
      .number({ message: "errors.amh.value.invalid" })
      .nullable()
      .optional(),
    "fsh.date": z.date().nullable().optional(),
    "fsh.value": z
      .number({ message: "errors.fsh.value.invalid" })
      .nullable()
      .optional(),
    "afc.left": z
      .number({ message: "errors.afc.oocytes.invalid" })
      .int()
      .nullable()
      .optional(),
    "afc.right": z
      .number({ message: "errors.afc.oocytes.invalid" })
      .int()
      .nullable()
      .optional(),
    "afc.date": z.date().nullable().optional(),
    "retrieval.count": z
      .number({ message: "errors.retrieval.count.invalid" })
      .int()
      .min(0, "errors.retrieval.count.invalid")
      .nullable()
      .optional(),
    "retrieval.date": z.date().nullable().optional(),
  });
};

export const imagesSchema: ZodSchema<OocyteUpload[]> = z
  .array(
    z.object({
      fileUrl: z.string(),
      hph: z
        .number({ message: "form.errors.image.hph.required" })
        .min(0, { message: "form.errors.image.hph.required" })
        .max(16, { message: "form.errors.image.hph.required" })
        .nullable(),
      condition: z.enum(
        Object.keys(OocyteCondition) as [keyof typeof OocyteCondition]
      , {message: "form.errors.image.condition.required"}).nullable(),
      originalName: z.string(),
      signedUrl: z.string().optional(),
      status: z.enum(["init", "uploading", "uploaded", "error"]),
    })
  )
  .min(1, { message: "form.errors.image.upload" });

export const scoretoColor = (score: number) => {
  if (score <= 20) return "#FEF08A";
  if (score <= 35) return "#FDBA74";
  if (score <= 60) return "#C4B5FD";
  return "#C1DCA0";
};
